import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Switch, Space, message, Input as AntInput } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, BankOutlined } from '@ant-design/icons';
import axiosInstance from '../../utils/axios'; // Adjust path if needed
import PageHeading from '../../components/PageHeading';

const FlashNews = () => {
  const [flashNews, setFlashNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [form] = Form.useForm();

  const fetchFlashNews = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params = { page, search };
      const response = await axiosInstance.get('news_and_events/flash-news/', { params });
      setFlashNews(response.data.results || response.data);
      setPagination({
        current: page,
        pageSize: 10,
        total: response.data.count || 0,
      });
    } catch (error) {
      message.error('Failed to fetch flash news');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlashNews(1, searchText);
  }, [searchText]);

  const handleTableChange = (pagination) => {
    fetchFlashNews(pagination.current, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`news_and_events/flash-news/${id}`);
      message.success('Flash news deleted successfully');
      fetchFlashNews(pagination.current, searchText);
    } catch (error) {
      message.error('Failed to delete flash news');
    }
  };

  const handleEdit = (record) => {
    setEditingNews(record);
    form.setFieldsValue({
      title: record.title,
      content: record.content,
      publish: record.publish,
    });
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingNews(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingNews) {
        // Update
        await axiosInstance.put(`news_and_events/flash-news/${editingNews.id}/`, values);
        message.success('Flash news updated successfully');
      } else {
        // Create
        await axiosInstance.post('news_and_events/flash-news/', values);
        message.success('Flash news added successfully');
      }
      setIsModalOpen(false);
      fetchFlashNews(pagination.current, searchText);
    } catch (error) {
      message.error('Failed to save flash news');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: 'Published',
      dataIndex: 'publish',
      key: 'publish',
      render: (publish) => (
        <span style={{ color: publish ? 'green' : 'red' }}>
          {publish ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="dashboard">
      <PageHeading name="Flash News Management" icon={<BankOutlined />} />

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <AntInput.Search
          placeholder="Search title or content"
          onSearch={handleSearch}
          style={{ width: 300 }}
          allowClear
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Flash News
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={flashNews}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
      />

      <Modal
        title={editingNews ? 'Edit Flash News' : 'Add Flash News'}
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={() => setIsModalOpen(false)}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter title' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="content"
            label="Content"
            rules={[{ required: true, message: 'Please enter content' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="publish" label="Publish" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FlashNews;